/**
 * Native bridges.
 *
 * Every function here is safe to call on the web: it resolves to a no-op or a
 * sensible default rather than throwing, so page code never needs to branch on
 * platform. Plugins are loaded lazily so the web bundle stays small.
 */
import { isNative, platform } from './supabase.js';

const plugin = (name) => window.Capacitor?.Plugins?.[name] ?? null;

// ---------------------------------------------------------------- device id
/** OS-provided identifier on native; a persisted random id on web. */
export async function nativeDeviceId() {
  if (!isNative()) return null;
  const Device = plugin('Device');
  if (!Device) return null;
  const { identifier } = await Device.getId();
  return identifier ?? null;
}

export async function nativeDeviceInfo() {
  if (!isNative()) return null;
  const Device = plugin('Device');
  if (!Device) return null;
  const info = await Device.getInfo();
  return {
    label: `${info.name ?? info.model ?? 'Device'}`,
    kind: info.platform === 'ios' && /iPad/i.test(info.model ?? '') ? 'tablet' : 'mobile',
    platform: info.platform,
    userAgent: `${info.manufacturer ?? ''} ${info.model ?? ''} ${info.operatingSystem ?? ''} ${info.osVersion ?? ''}`.trim(),
  };
}

// ---------------------------------------------------------------- push
/**
 * Request permission, fetch the FCM token, hand it to the server.
 * Returns the token, or null if unavailable or declined.
 */
export async function registerPush(deviceId, api) {
  if (!isNative()) return null;                 // web push is a separate path
  const FCM = plugin('FirebaseMessaging');
  if (!FCM) return null;

  const { receive } = await FCM.requestPermissions();
  if (receive !== 'granted') return null;

  const { token } = await FCM.getToken();
  if (!token) return null;

  await api.registerPushToken(deviceId, token);

  // FCM rotates tokens; keep the server current or notifications go nowhere.
  FCM.addListener?.('tokenReceived', ({ token: fresh }) => {
    if (fresh) api.registerPushToken(deviceId, fresh).catch(() => {});
  });
  return token;
}

/**
 * Taps on a push (or on one of its action buttons). Fires when the app is
 * opened from the notification, including from a cold start. `actionId` is
 * 'tap' for the notification body, otherwise the registered action id.
 */
export function onPushAction(cb) {
  if (!isNative()) return () => {};
  const FCM = plugin('FirebaseMessaging');
  if (!FCM?.addListener) return () => {};
  const h = FCM.addListener('notificationActionPerformed', (e) => {
    cb({ actionId: e.actionId ?? 'tap', data: e.notification?.data ?? {} });
  });
  return () => Promise.resolve(h).then((x) => x?.remove?.());
}

// ---------------------------------------------------------------- location
/**
 * A navigator.geolocation-shaped source. On native it is backed by the
 * Capacitor plugin, which asks for the OS permission with our Info.plist
 * strings and works from any page origin — WebKit refuses the browser API on
 * insecure origins such as the live-reload http://<mac>:5173. On the web it is
 * navigator.geolocation itself (or null when the browser has none).
 */
export function geolocationSource() {
  if (!isNative()) return navigator.geolocation ?? null;
  const Geo = plugin('Geolocation');
  if (!Geo) return navigator.geolocation ?? null;

  const DENIED = { code: 1, PERMISSION_DENIED: 1, message: 'Location permission denied' };
  const asError = (e) => (/denied|not authorized|permission/i.test(e?.message ?? '') ? DENIED : { code: 2, PERMISSION_DENIED: 1, message: e?.message ?? 'unavailable' });
  const ensure = async () => {
    let { location } = await Geo.checkPermissions();
    if (location === 'prompt' || location === 'prompt-with-rationale') ({ location } = await Geo.requestPermissions());
    if (location !== 'granted') throw DENIED;
  };
  return {
    getCurrentPosition(ok, fail, opts = {}) {
      ensure().then(() => Geo.getCurrentPosition(opts)).then(ok).catch((e) => fail?.(asError(e)));
    },
    watchPosition(ok, fail, opts = {}) {
      // The plugin's id arrives asynchronously; clearWatch waits for it.
      const id = ensure().then(() => Geo.watchPosition(opts, (pos, err) => (err ? fail?.(asError(err)) : pos && ok(pos))));
      id.catch((e) => fail?.(asError(e)));
      return id;
    },
    clearWatch(id) { Promise.resolve(id).then((i) => i && Geo.clearWatch({ id: i })).catch(() => {}); },
  };
}


/**
 * Native geolocation. The browser API works inside the webview, but the plugin
 * handles iOS/Android permission prompts properly and can keep running when the
 * app is backgrounded — which the "walk to a field to recharge" loop depends on.
 */
export async function nativePosition({ highAccuracy = true, timeout = 8000 } = {}) {
  if (!isNative()) return null;
  const Geo = plugin('Geolocation');
  if (!Geo) return null;
  const perm = await Geo.checkPermissions();
  if (perm.location === 'denied') return null;
  if (perm.location !== 'granted') {
    const asked = await Geo.requestPermissions();
    if (asked.location !== 'granted') return null;
  }
  const pos = await Geo.getCurrentPosition({ enableHighAccuracy: highAccuracy, timeout });
  return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
}

// ---------------------------------------------------------------- shell
/**
 * The native splash stays up (launchAutoHide: false) until the session is
 * resolved, so the user never sees the web loading spinner between the OS
 * launch image and the first real screen.
 */
export async function hideSplash() {
  if (!isNative()) return;
  await plugin('SplashScreen')?.hide({ fadeOutDuration: 250 }).catch(() => {});
}

/** Keep the status bar legible on the current theme; no-op on the web. */
export async function applyStatusBar(theme) {
  if (!isNative()) return;
  const StatusBar = plugin('StatusBar');
  if (!StatusBar) return;
  const dark = theme === 'dark';
  // Capacitor's naming is by background: Light = light bar with dark text.
  await StatusBar.setStyle({ style: dark ? 'DARK' : 'LIGHT' }).catch(() => {});
  if (platform() === 'android') {
    await StatusBar.setBackgroundColor({ color: dark ? '#1b1815' : '#f5f0e8' }).catch(() => {});
  }
}

// ---------------------------------------------------------------- auth
/**
 * Google / Apple sign-in through the native sheet, exchanged for a Supabase
 * session via signInWithIdToken.
 *
 * Note for Apple: pass NO nonce on either side. A mismatched nonce is the usual
 * cause of Apple sign-in failing here.
 */
export async function nativeSocialSignIn(provider, supabase) {
  const Social = plugin('SocialLogin');
  if (!isNative() || !Social) return null;

  await Social.initialize?.({ [provider]: providerConfig(provider) });
  const res = await Social.login({
    provider,
    options: provider === 'apple' ? { scopes: ['email', 'name'] } : { scopes: ['profile', 'email'] },
  });
  const token = res?.result?.idToken;
  if (!token) throw new Error(`${provider} sign-in returned no idToken`);

  const { data, error } = await supabase.auth.signInWithIdToken({ provider, token });
  if (error) throw error;
  return data;
}

/**
 * Per-provider init options for @capgo/capacitor-social-login.
 *
 * Google needs the OAuth client ids from Google Cloud (created by Firebase):
 *   - webClientId  — the "Web application" client. It is the audience of the
 *     id token on Android AND (via iOSServerClientId) on iOS, and it is the
 *     client id Supabase validates against. Never pass the Android client id
 *     here; Android is authorised purely by package name + signing SHA-1.
 *   - iOSClientId  — the "iOS" client (CLIENT_ID in GoogleService-Info.plist).
 * Both come from the Firebase config files; scripts/google-signin-ids.sh
 * copies them into the env files. Apple needs nothing: the bundle id is the
 * client id and Supabase already lists it.
 */
function providerConfig(provider) {
  if (provider !== 'google') return {};
  const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;
  const iOSClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID;
  if (!webClientId || (platform() === 'ios' && !iOSClientId)) {
    throw new Error('Google sign-in is not configured for this build (missing VITE_GOOGLE_*_CLIENT_ID)');
  }
  return { webClientId, iOSClientId, iOSServerClientId: webClientId, mode: 'online' };
}

export const nativePlatform = platform;
